import { Formik } from "formik";
import { saveAs } from "file-saver";
import _ from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { FaBars } from "react-icons/fa";
import { RiDeleteBinLine } from "react-icons/ri";
import { FiDownload } from "react-icons/fi";
import { List, arrayMove } from "react-movable";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ReactSelect from "react-select";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import CustomTabs from "../../components/CustomTabs";
import Input from "../../components/input";
import PhoneNumberInput from "../../components/PhoneNumberInput";
import LogsComments from "../../components/LogsComments";
import StatusModal from "../../components/modals/StatusModal";
import TripCloseModal from "../../components/modals/TripCloseModal";
import TripCompleteModal from "../../components/modals/TripCompleteModal";
import Select from "../../components/Select";
import Status from "../../components/Status";
import { apiService } from "../../services/apiService";
import {
  fetchBanksDropdown,
  fetchCostCenterDropdown,
  fetchCustomerDropdown,
  fetchProductsDropdown,
  fetchUsersDropdown,
  fetchVehiclesTrackingPlusDropdown
} from "../../store/Slices/dropdownSlice";
import {
  actualTripComplete,
  changeTripStatus,
  changeStopOrders,
  fetchTripTimeline,
  getTripDetails,
  setSelectedTripId,
  setViewAllTrips,
  tripUpdate,
} from "../../store/Slices/TripSlice";
import {
  formatDate,
  formatUtcDateTimeForApi,
  formatUtcDateTimeForInput,
  tripInitialValues,
  tripStyles
} from "../../utils/helper";
import { tripDetailSchema } from "../../utils/Schema";
import SpinnerLoader from "../../utils/SpinnerLoader";
import {
  activeDetailsIcon,
  activeHistoryIcon,
  activeInspectionIcon,
  activeViolationsIcon,
  detailsIcon,
  historyIcon,
  inspectionIcon,
  mapViewActiveIcon,
  mapViewIcon,
  statusIcon,
  trackerActiveIcon,
  trackerIcon,
  violationsIcon
} from "../../utils/svgs";
import { errorNotify, successNotify } from "../../utils/toast";
import TripExpenses from "./TripExpenses";
import TripHistoryTimeline from "./TripHistoryTimeline";
import TripInspection from "./TripInspection";
import TripMap from "./TripMap";
import TripPettyCash from "./TripPettyCash";
import TripTracker from "./TripTracker";
import TripViolations from "./TripViolations";
import "../../styles/Trip.css";

const stopTypeOptions = [
  { label: "Load", value: "load" },
  { label: "Unload", value: "unload" },
  { label: "Transfer", value: "transfer" },
  { label: "Return", value: "return" },
  { label: "Empty", value: "empty" },
];

const stopLoadTypeOptions = [
  { label: "Dry Cargo", value: "dry_cargo" },
  { label: "Liquid Cargo", value: "liquid_cargo" },
];

const getBlankStop = () => ({
  id: null,
  location: {
    location_id: "",
    location_point: "",
    location_latitude: "",
    location_longitude: "",
    province: "",
  },
  stopType: "",
  loadType: "",
  product: "",
  productValue: "",
  quantity: "",
});

const shouldRequireStopProductDetails = (stopType = "") => {
  const normalizedStopType = String(stopType || "").trim().toLowerCase();
  return normalizedStopType !== "empty" && normalizedStopType !== "return";
};


const normalizeLookupValue = (value) => String(value ?? "").trim().toLowerCase();

const getFirstDefinedValue = (candidates, fallback = "") => {
  for (const candidate of candidates) {
    if (candidate === "" || candidate === null || candidate === undefined) {
      continue;
    }

    return candidate;
  }

  return fallback;
};

const stripTrailingIdentifier = (value = "") =>
  String(value || "")
    .replace(/\s*\([^)]*\)\s*$/u, "")
    .trim();

const toCleanText = (value) => String(value ?? "").trim();
const isTextLikeValue = (value) => !_.isNil(value) && !_.isObject(value) && toCleanText(value) !== "";

const pickText = (source, paths = [], fallback = "") =>
  toCleanText(
    _.find(
      paths.map((path) => _.get(source, path)),
      isTextLikeValue
    ) ?? fallback
  );

const pickFirstText = (values = [], fallback = "") =>
  toCleanText(_.find(values, isTextLikeValue) ?? fallback);

const buildWarehouseLocationLabel = (warehouseName = "", city = "", fallback = "") =>
  [toCleanText(warehouseName), toCleanText(city)].filter(Boolean).join(" - ") ||
  toCleanText(fallback);

const locationIdPaths = [
  "id",
  "value",
  "location_id",
  "customer_warehouse_id",
  "warehouse_id",
  "city_id",
  "location.id",
  "location.location_id",
  "location.customer_warehouse_id",
  "location.warehouse_id",
  "customer_warehouse.id",
  "customer_warehouse.location_id",
  "customer_warehouse.customer_warehouse_id",
  "customer_warehouse.warehouse_id",
  "warehouse.id",
  "warehouse.location_id",
  "warehouse.customer_warehouse_id",
  "warehouse.warehouse_id",
];

const stopLocationIdPaths = [
  "location_id",
  "customer_warehouse_id",
  "warehouse_id",
  "city_id",
  "location.id",
  "location.location_id",
  "location.customer_warehouse_id",
  "location.warehouse_id",
  "customer_warehouse.id",
  "customer_warehouse.location_id",
  "customer_warehouse.customer_warehouse_id",
  "customer_warehouse.warehouse_id",
  "warehouse.id",
  "warehouse.location_id",
  "warehouse.customer_warehouse_id",
  "warehouse.warehouse_id",
];

const getLocationIds = (source = {}) =>
  [
    ...locationIdPaths.map((path) => _.get(source, path)),
    ...(Array.isArray(source?.idCandidates) ? source.idCandidates : []),
  ]
    .map(toCleanText)
    .filter(Boolean);

const getStopLocationIds = (stop = {}) =>
  stopLocationIdPaths.map((path) => toCleanText(_.get(stop, path))).filter(Boolean);

const getLocationTextKeys = (source = {}) =>
  [
    source?.address,
    source?.label,
    source?.location?.location_point,
    typeof source?.location === "string" ? source.location : "",
  ]
    .map(normalizeLookupValue)
    .filter((key) => key && key !== "location");

const unwrapApiPayload = (response) => response?.data?.data || response?.data || response || {};

const getArrayFromPaths = (source = {}, paths = []) =>
  Array.isArray(source)
    ? source
    : _.find(paths.map((path) => _.get(source, path)), Array.isArray) || [];

const resolveDropdownCollection = (source, candidateKeys = []) => {
  if (Array.isArray(source)) return source;
  if (!source || typeof source !== "object") return [];

  const normalizedCandidateKeys = candidateKeys.map((key) =>
    String(key || "").toLowerCase()
  );

  for (const [key, value] of Object.entries(source)) {
    if (!Array.isArray(value)) continue;

    const normalizedKey = String(key || "").toLowerCase();
    if (
      normalizedCandidateKeys.includes(normalizedKey) ||
      normalizedCandidateKeys.some((candidate) => normalizedKey.includes(candidate))
    ) {
      return value;
    }
  }

  return [];
};

const getCustomerTripLocations = (detail = {}) =>
  getArrayFromPaths(detail, [
    "locations",
    "locations.data",
    "warehouses",
    "warehouses.data",
    "customer_locations",
    "customer_locations.data",
    "customer_warehouses",
    "customer_warehouses.data",
    "data.locations",
    "data.locations.data",
    "data.warehouses",
    "data.warehouses.data",
    "data.customer_locations",
    "data.customer_locations.data",
    "data.customer_warehouses",
    "data.customer_warehouses.data",
  ]);

const mapCustomerContractsToOptions = (contracts = []) =>
  (Array.isArray(contracts) ? contracts : []).map((contract) => {
    const contractId = getFirstDefinedValue([
      contract?.id,
      contract?.value,
      contract?.contract_id,
      contract?.contractId,
    ]);
    const contractName = stripTrailingIdentifier(
      getFirstDefinedValue([
        contract?.name,
        contract?.label,
        contract?.title,
        contract?.contract_name,
        contract?.contractName,
        contract?.contract_title,
        contract?.display_name,
        contract?.displayName,
      ], "")
    );

    return {
      value: contractId,
      label: contractName || `Contract ${contractId}`,
    };
  });

const mapCustomerLocationsToOptions = (locations = []) =>
  (Array.isArray(locations) ? locations : []).map((location) => {
    const warehouseName = pickText(location, [
      "warehouse_name",
      "name",
      "label",
      "customer_warehouse.warehouse_name",
      "customer_warehouse.name",
      "warehouse.warehouse_name",
      "warehouse.name",
    ]);
    const city = pickText(location, ["city", "customer_warehouse.city", "warehouse.city"]);
    const address = pickText(location, [
      "address",
      "location",
      "customer_warehouse.address",
      "customer_warehouse.location",
      "warehouse.address",
      "warehouse.location",
    ], warehouseName);
    const idCandidates = getLocationIds(location);
    const label = buildWarehouseLocationLabel(warehouseName, city, address || "Location");

    return {
      value: idCandidates[0] || address || label,
      label,
      address,
      idCandidates,
      latitude: pickText(location, [
        "lat",
        "latitude",
        "customer_warehouse_latitude",
        "customer_warehouse.lat",
        "customer_warehouse.latitude",
        "warehouse.lat",
        "warehouse.latitude",
      ]),
      longitude: pickText(location, [
        "lng",
        "longitude",
        "customer_warehouse_longitude",
        "customer_warehouse.lng",
        "customer_warehouse.longitude",
        "warehouse.lng",
        "warehouse.longitude",
      ]),
      province: pickText(location, [
        "province",
        "state",
        "customer_warehouse.province",
        "customer_warehouse.state",
        "warehouse.province",
        "warehouse.state",
      ]),
    };
  });

const mapCustomerProductsToOptions = (products = []) =>
  (Array.isArray(products) ? products : []).map((product) => {
    const productName = String(
      getFirstDefinedValue([product?.name, product?.label], "")
    ).trim();
    const productUnit = String(getFirstDefinedValue([product?.unit], "")).trim();

    return {
      ...product,
      value: String(
        getFirstDefinedValue([product?.id, product?.value, product?.product_id], "")
      ).trim(),
      label: [productName, productUnit ? `(${productUnit})` : ""]
        .filter(Boolean)
        .join(" ")
        .trim() || "Product",
      productValue: String(
        getFirstDefinedValue([product?.product_rate, product?.product_value, product?.rate], "")
      ).trim(),
    };
  });

const getTripProductRateDateForApi = (dateTimeValue = "") => {
  const normalizedValue = String(dateTimeValue || "").trim().replace(" ", "T");
  if (!normalizedValue) return "";

  const dateMatch = normalizedValue.match(/^(\d{4}-\d{2}-\d{2})/u);
  if (dateMatch) return dateMatch[1];

  const parsedDate = new Date(normalizedValue);
  if (Number.isNaN(parsedDate.getTime())) return "";

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const mapContractVehicleTypesToOptions = (vehicleTypes = []) =>
  (Array.isArray(vehicleTypes) ? vehicleTypes : [])
    .map((vehicleType) => {
      const vehicleTypeId = String(
        getFirstDefinedValue([
          vehicleType?.id,
          vehicleType?.value,
          vehicleType?.vehicle_type_id,
        ], "")
      ).trim();
      const vehicleTypeTitle = String(
        getFirstDefinedValue([
          vehicleType?.title,
          vehicleType?.name,
          vehicleType?.label,
        ], "")
      ).trim();

      if (!vehicleTypeId && !vehicleTypeTitle) {
        return null;
      }

      return {
        value: vehicleTypeId,
        label: vehicleTypeTitle || `Vehicle Type ${vehicleTypeId}`,
      };
    })
    .filter(Boolean);

const buildLocationValueFromOption = (option) => ({
  location_id: toCleanText(getFirstDefinedValue([option?.value], "")),
  location_point: toCleanText(getFirstDefinedValue([option?.label, option?.address], "")),
  location_latitude: toCleanText(getFirstDefinedValue([option?.latitude], "")),
  location_longitude: toCleanText(getFirstDefinedValue([option?.longitude], "")),
  province: toCleanText(getFirstDefinedValue([option?.province], "")),
});

const buildMinimumStops = (minimumStops = 2) =>
  Array.from({ length: Math.max(Number(minimumStops) || 0, 2) }, () => getBlankStop());

const findProductRateOption = (rawValue, sourceOptions = []) => {
  const needle = normalizeLookupValue(rawValue);
  if (!needle) return null;

  return (
    (Array.isArray(sourceOptions) ? sourceOptions : []).find((option) => {
      const candidates = [
        option?.value,
        option?.id,
        option?.product_id,
        option?.product,
        option?.label,
        option?.name,
        option?.title,
      ]
        .map(normalizeLookupValue)
        .filter(Boolean);

      return candidates.includes(needle);
    }) || null
  );
};

const syncStopsWithProductRateOptions = (
  stops = [],
  productOptions = [],
  { preserveMissingProduct = true } = {}
) => {
  const safeStops = Array.isArray(stops) && stops.length ? stops : buildMinimumStops();
  const safeProductOptions = Array.isArray(productOptions) ? productOptions : [];
  const defaultProduct = safeProductOptions?.[0] || null;

  return safeStops.map((stop) => {
    const normalizedStopType = String(stop?.stopType || "").trim();

    if (!normalizedStopType || !shouldRequireStopProductDetails(normalizedStopType)) {
      return {
        ...stop,
        product: "",
        productValue: "",
      };
    }

    const selectedProduct = findProductRateOption(stop?.product, safeProductOptions);

    if (selectedProduct) {
      return {
        ...stop,
        product: String(selectedProduct.value || ""),
        productValue: String(selectedProduct.productValue || ""),
      };
    }

    if (stop?.product && preserveMissingProduct) {
      return stop;
    }

    return {
      ...stop,
      product: defaultProduct ? String(defaultProduct.value || "") : "",
      productValue: defaultProduct ? String(defaultProduct.productValue || "") : "",
    };
  });
};

const findLocationOption = (stop = {}, locationOptions = []) => {
  const options = Array.isArray(locationOptions) ? locationOptions : [];
  const stopIds = getStopLocationIds(stop).map(normalizeLookupValue);

  if (stopIds.length) {
    return (
      options.find((option) =>
        getLocationIds(option).map(normalizeLookupValue).some((id) => stopIds.includes(id))
      ) || null
    );
  }

  const stopTexts = getLocationTextKeys(stop);
  if (!stopTexts.length) return null;

  return (
    options.find((option) => getLocationTextKeys(option).some((text) => stopTexts.includes(text))) ||
    null
  );
};

const buildStopLocationValue = (stop = {}) => {
  const rawLocation = stop?.location;
  const warehouseName = pickFirstText([
    stop?.warehouse_name,
    stop?.customer_warehouse?.warehouse_name,
    stop?.customer_warehouse?.name,
    stop?.warehouse?.warehouse_name,
    stop?.warehouse?.name,
    rawLocation?.warehouse_name,
    rawLocation?.name,
    typeof rawLocation === "string" ? rawLocation : "",
  ]);
  const city = pickFirstText([
    stop?.city,
    stop?.customer_warehouse?.city,
    stop?.warehouse?.city,
    rawLocation?.city,
  ]);
  const fallbackLocationText = pickFirstText([
    stop?.location_point,
    stop?.location_name,
    stop?.address,
    rawLocation?.address,
    rawLocation?.location,
    typeof rawLocation === "string" ? rawLocation : "",
  ]);

  return {
    location_id: getStopLocationIds(stop)[0] || "",
    location_point: buildWarehouseLocationLabel(warehouseName, city, fallbackLocationText),
    location_latitude: pickFirstText([
      stop?.latitude,
      stop?.lat,
      stop?.location_latitude,
      rawLocation?.latitude,
      rawLocation?.lat,
    ]),
    location_longitude: pickFirstText([
      stop?.longitude,
      stop?.lng,
      stop?.location_longitude,
      rawLocation?.longitude,
      rawLocation?.lng,
    ]),
    province: pickFirstText([
      stop?.province,
      stop?.customer_warehouse?.province,
      stop?.customer_warehouse?.warehouse?.province,
      stop?.warehouse?.province,
      rawLocation?.province,
      rawLocation?.state,
      stop?.state,
      stop?.customer_warehouse?.state,
      stop?.warehouse?.state,
    ]),
  };
};

const normalizeTripStop = (stop = {}) => {
  const stopProductValue =
    stop?.product_id ??
    stop?.product?.id ??
    stop?.product?.product_id ??
    stop?.product?.value ??
    stop?.product?.name ??
    "";

  return {
    id: stop?.id || null,
    location: buildStopLocationValue(stop),
    stopType: stop?.stop_type || "",
    loadType: stop?.load_type || "",
    product: toCleanText(stopProductValue),
    productValue: pickFirstText([
      stop?.product_load_value,
      stop?.product_value,
      stop?.product_rate,
      stop?.product?.product_rate,
      stop?.product?.product_value,
      stop?.product?.rate,
    ]),
    quantity: stop?.quantity ? String(stop.quantity) : "",
  };
};

const getAvailableLocationOptions = (stopIndex, stops = [], locationOptions = []) => {
  const currentStopIds = getStopLocationIds(stops?.[stopIndex]).map(normalizeLookupValue);
  const selectedOtherLocationIds = (Array.isArray(stops) ? stops : [])
    .flatMap((stop, index) =>
      index === stopIndex ? [] : getStopLocationIds(stop).map(normalizeLookupValue)
    )
    .filter(Boolean);

  return (Array.isArray(locationOptions) ? locationOptions : []).filter((option) => {
    const optionIds = getLocationIds(option).map(normalizeLookupValue).filter(Boolean);

    if (!optionIds.length) return true;

    return (
      optionIds.some((id) => currentStopIds.includes(id)) ||
      !optionIds.some((id) => selectedOtherLocationIds.includes(id))
    );
  });
};

const getTripContractId = (detail = {}) =>
  String(
    getFirstDefinedValue([
      detail?.contract_id,
      detail?.data?.contract_id,
      detail?.contractId,
      detail?.data?.contractId,
      detail?.contract?.id,
      detail?.contract?.contract_id,
      detail?.contract?.value,
      detail?.data?.contract?.id,
      detail?.data?.contract?.contract_id,
      detail?.data?.contract?.value,
    ], "")
  ).trim();

const getTripCustomerId = (detail = {}) =>
  String(
    getFirstDefinedValue([
      detail?.customer_id,
      detail?.data?.customer_id,
      detail?.customer?.id,
      detail?.customer?.customer_id,
      detail?.customer?.value,
      detail?.data?.customer?.id,
      detail?.data?.customer?.customer_id,
      detail?.data?.customer?.value,
    ], "")
  ).trim();

const buildTripCustomerOption = (tripData = {}, fallbackId = "") => {
  const customer = tripData?.customer || tripData?.data?.customer || {};
  const value = String(
    getFirstDefinedValue([
      fallbackId,
      tripData?.customer_id,
      tripData?.data?.customer_id,
      customer?.id,
      customer?.customer_id,
      customer?.value,
    ], "")
  ).trim();
  const label = String(
    getFirstDefinedValue([
      customer?.name,
      customer?.label,
      customer?.customer_name,
      customer?.customerName,
      customer?.title,
      customer?.display_name,
      customer?.displayName,
      tripData?.customer_name,
      tripData?.customerName,
      tripData?.data?.customer_name,
      tripData?.data?.customerName,
    ], "")
  ).trim();

  if (!value && !label) return null;

  return {
    ...customer,
    value: value || label,
    label: label || `Customer ${value}`,
  };
};

const buildTripContractOption = (tripData = {}, fallbackId = "") => {
  const contract = tripData?.contract || tripData?.data?.contract || {};
  const value = String(
    getFirstDefinedValue([
      fallbackId,
      tripData?.contract_id,
      tripData?.data?.contract_id,
      contract?.id,
      contract?.contract_id,
      contract?.value,
    ], "")
  ).trim();
  const label = stripTrailingIdentifier(
    getFirstDefinedValue([
      contract?.name,
      contract?.label,
      contract?.title,
      contract?.contract_name,
      contract?.contractName,
      contract?.contract_title,
      contract?.display_name,
      contract?.displayName,
      tripData?.contract_name,
      tripData?.contractName,
      tripData?.data?.contract_name,
      tripData?.data?.contractName,
    ], "")
  );

  if (!value && !label) return null;

  return {
    ...contract,
    value: value || label,
    label: label || `Contract ${value}`,
  };
};

const normalizeSelectOption = (option = {}, fallbackLabel = "Option") => {
  const value = String(
    getFirstDefinedValue([
      option?.value,
      option?.id,
      option?.customer_id,
      option?.customerId,
      option?.contract_id,
      option?.contractId,
    ], "")
  ).trim();
  const label = String(
    getFirstDefinedValue([
      option?.label,
      option?.name,
      option?.title,
      option?.customer_name,
      option?.customerName,
      option?.contract_name,
      option?.contractName,
      option?.contract_title,
      option?.display_name,
      option?.displayName,
      value ? `${fallbackLabel} ${value}` : "",
    ], "")
  ).trim();

  return {
    ...option,
    value,
    label,
  };
};

const mergeSelectedOption = (options = [], selectedOption = null) => {
  const optionMap = new Map();

  [selectedOption, ...(Array.isArray(options) ? options : [])]
    .filter(Boolean)
    .forEach((option) => {
      const value = String(option?.value ?? "").trim();
      if (!value) return;
      optionMap.set(value, option);
    });

  return Array.from(optionMap.values());
};

const findSelectedOption = (options = [], selectedValue = "") => {
  const value = String(selectedValue ?? "").trim();
  if (!value) return null;

  return (
    (Array.isArray(options) ? options : []).find(
      (option) => String(option?.value ?? "").trim() === value
    ) || null
  );
};

const normalizeTripStatusValue = (value) =>
  String(value || "").trim().toLowerCase();

const formatStatusLabel = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const isTripCompletionStatus = (value) => {
  const normalizedStatus = normalizeTripStatusValue(value);
  return normalizedStatus === "completed" || normalizedStatus === "driver_completed";
};

const applyStopTypeDependencies = (index, nextStopType, setFieldValue, setFieldTouched) => {
  setFieldValue(`stops[${index}].stopType`, nextStopType);

  if (!shouldRequireStopProductDetails(nextStopType)) {
    setFieldValue(`stops[${index}].loadType`, "");
    setFieldValue(`stops[${index}].product`, "");
    setFieldValue(`stops[${index}].productValue`, "");
    setFieldValue(`stops[${index}].quantity`, "");
    setFieldTouched(`stops[${index}].loadType`, false, false);
    setFieldTouched(`stops[${index}].product`, false, false);
    setFieldTouched(`stops[${index}].quantity`, false, false);
  }
};

const normalizePhoneNumberValue = (value) => {
  const digits = String(value || "").replace(/\D/g, "");

  if (!digits) return "";
  if (digits.startsWith("92")) return digits.slice(0, 12);
  if (digits.startsWith("0") && digits.length === 11) return `92${digits.slice(1)}`;

  return digits.slice(0, 12);
};

const buildTripUserOption = (user, fallbackId = "") => {
  const value = String(
    getFirstDefinedValue([fallbackId, user?.id, user?.value], "")
  ).trim();
  const label = String(
    getFirstDefinedValue([user?.username, user?.label, user?.name, user?.email], "")
  ).trim();

  if (!value && !label) return null;

  return {
    value: value || label,
    label: label || `User ${value}`,
    image: user?.image || null,
    showIcon: true,
  };
};

const mergeTripUserOptions = (baseOptions = [], tripData = {}) => {
  const optionMap = new Map();
  const combinedOptions = [
    ...(Array.isArray(baseOptions) ? baseOptions : []),
    buildTripUserOption(tripData?.primary_driver, tripData?.primary_driver_id),
    buildTripUserOption(tripData?.secondary_driver, tripData?.secondary_driver_id),
    buildTripUserOption(tripData?.helper, tripData?.helper_id),
  ].filter(Boolean);

  combinedOptions.forEach((option) => {
    const value = String(option?.value || "").trim();
    if (!value) return;

    optionMap.set(value, {
      ...option,
      value,
      label: option?.label || `User ${value}`,
      showIcon: option?.showIcon ?? true,
    });
  });

  return Array.from(optionMap.values());
};

const findTripUserOption = (options = [], selectedValue = "") => {
  const value = String(selectedValue ?? "").trim();
  if (!value) return null;

  return (
    (Array.isArray(options) ? options : []).find(
      (option) => String(option?.value ?? "").trim() === value
    ) || null
  );
};

const formatLocalDateTime = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  if (hours === 0) hours = 12;

  return `${day}/${month}/${year} ${hours}:${minutes} ${ampm}`;
};

const hasDateTimePassed = (value) => {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  return date.getTime() <= Date.now();
};

const isEndBeforeStart = (startValue, endValue) => {
  if (!startValue || !endValue) return false;

  const start = new Date(startValue);
  const end = new Date(endValue);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;

  return end.getTime() < start.getTime();
};

const isEndOdometerNotGreaterThanStart = (startValue, endValue) => {
  if (endValue === "" || endValue === null || endValue === undefined) return false;
  if (startValue === "" || startValue === null || startValue === undefined) return false;

  const start = Number(startValue);
  const end = Number(endValue);

  if (Number.isNaN(start) || Number.isNaN(end)) return false;

  return end <= start;
};

const parseNullableNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? null : numericValue;
};

const parseNumberOrZero = (value) => {
  if (value === "" || value === null || value === undefined) return 0;
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? 0 : numericValue;
};

const getNumericInputValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return String(value);
};

const parseIdOrRaw = (value) => {
  if (value === "" || value === null || value === undefined) return 0;
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? value : numericValue;
};

const resolveStopLocationId = (stop = {}, locationOptions = []) => {
  const explicitLocationId = String(
    getFirstDefinedValue([
      stop?.location?.location_id,
      stop?.location_id,
      stop?.customer_warehouse_id,
      stop?.warehouse_id,
      stop?.location?.id,
      stop?.location?.customer_warehouse_id,
      stop?.location?.warehouse_id,
    ], "")
  ).trim();

  if (explicitLocationId) return explicitLocationId;

  const matchedOption = findLocationOption(stop, locationOptions);
  return String(getFirstDefinedValue([matchedOption?.value], "")).trim();
};

const buildTripPayload = (values, tripId, currentStatus, locationOptions = [], productOptions = []) => {
  const tripStops = (values?.stops || []).map((stop, index) => {
    const selectedProduct = (productOptions || []).find(
      (opt) => String(opt.value) === String(stop?.product)
    );
    const resolvedProductValue = stop?.productValue || selectedProduct?.productValue || "";

    return {
      stop_number: index + 1,
      product_id: parseIdOrRaw(stop?.product),
      quantity: stop?.quantity === "" || stop?.quantity === null || stop?.quantity === undefined
        ? 0
        : String(stop.quantity),
      stop_type: stop?.stopType || "",
      load_type: stop?.loadType || "",
      location_id: parseIdOrRaw(resolveStopLocationId(stop, locationOptions)),
      location: stop?.location?.location_point || "",
      latitude: parseNullableNumber(stop?.location?.location_latitude),
      longitude: parseNullableNumber(stop?.location?.location_longitude),
      province: stop?.location?.province || findLocationOption(stop, locationOptions)?.province || "",
      product_load_value: parseNullableNumber(resolvedProductValue),
    };
  });

  return {
    status: currentStatus,
    trip_id: tripId,
    description: values?.description || "",
    note: values?.notes || "",
    customer_id: values?.customer || "",
    contract_id: parseIdOrRaw(values?.contractId),
    vehicle_type_id: parseIdOrRaw(values?.vehicleType),
    cost_center_id: values?.costCenter || "",
    start_at: formatUtcDateTimeForApi(values?.startDateTime),
    expected_end_at: formatUtcDateTimeForApi(values?.expectedEndDateTime),
    actual_end_at: formatUtcDateTimeForApi(values?.actualEndDateTime),
    actual_at: formatUtcDateTimeForApi(values?.actualEndDateTime),
    trip_stops: tripStops,
    primary_driver_id: values?.firstDriver || "",
    primary_driver_contact: values?.firstDriverContact || "",
    secondary_driver_id: values?.supplementaryDriver || "",
    secondary_driver_contact: values?.supplementaryDriverContact || "",
    helper_id: values?.helper || "",
    helper_contact: values?.helperContact || "",
    vehicle_id: values?.vehicle || "",
    opening_fuel: parseNumberOrZero(values?.openingFuel),
    allocated_fuel: parseNumberOrZero(values?.allocatedFuel),
    closing_fuel: parseNumberOrZero(values?.closing_fuel),
    start_odm: parseNumberOrZero(values?.startOdometer),
    end_odm: parseNumberOrZero(values?.endOdometer),
    billing_frequency: values?.billingFrequency || "",
    payment_method: values?.paymentMethod || "",
    payment_term: values?.paymentTerms || "",
    bank_name: values?.bank || "",
    account_title: values?.accountTitle || "",
    iban_number: values?.iban || "",
    allocated_petty_cash: parseNumberOrZero(values?.allocatedPettyCash),
  };
};

const TripDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const { tripDetailsLoading: loading, tripDetailsData } = useSelector((state) => state.trips);
  const {
    customerDropdownData,
    costCenterDropdownData,
    vehiclesTrackingPlusDropdownData,
    usersDropdownData,
    productsDropdownData,
  } = useSelector((state) => state.dropdowns);

  const normalizedProductsDropdownData = useMemo(() => {
    return (productsDropdownData || []).map((item) => {
      const value =
        item?.value ??
        item?.id ??
        item?.product_id ??
        item?.product ??
        "";
      const label =
        item?.label ??
        item?.product ??
        item?.name ??
        item?.title ??
        String(value);

      return { ...item, value, label };
    });
  }, [productsDropdownData]);

  const normalizeText = (value) => String(value ?? "").trim().toLowerCase();

  const findProductOption = (rawValue, sourceOptions = normalizedProductsDropdownData) => {
    const needle = normalizeText(rawValue);
    if (!needle) return null;

    return (
      (Array.isArray(sourceOptions) ? sourceOptions : []).find((o) => {
        const candidates = [
          o?.value,
          o?.id,
          o?.product_id,
          o?.product,
          o?.label,
          o?.name,
          o?.title,
        ]
          .map(normalizeText)
          .filter(Boolean);

        return candidates.includes(needle);
      }) || null
    );
  };

  const [activeTab, setActiveTab] = useState("detail");
  const [downloadingReport, setDownloadingReport] = useState(false);

  const handleDownloadCloserReport = async () => {
    if (downloadingReport) return;
    setDownloadingReport(true);
    try {
      const response = await apiService.getTripCloserReport(id);
      const fileUrl = response?.data || response;
      if (fileUrl) {
        try {
          const res = await fetch(fileUrl);
          const blob = await res.blob();
          saveAs(blob, `trip-closure-${id}.pdf`);
          successNotify("Report download started successfully.");
        } catch (fetchErr) {
          // Fallback to direct URL download via file-saver
          saveAs(fileUrl, `trip-closure-${id}.pdf`);
          successNotify("Report download triggered.");
        }
      } else {
        errorNotify("Failed to get report download URL.");
      }
    } catch (error) {
      errorNotify(error || "Failed to download report");
    } finally {
      setDownloadingReport(false);
    }
  };

  const [initialValues, setInitialValues] = useState(tripInitialValues);
  const [status, setStatus] = useState(tripDetailsData?.status);
  const currentStatusRef = useRef(normalizeTripStatusValue(tripDetailsData?.status));
  const suppressClosedModalReopenRef = useRef(false);
  const lastClosedModalSuccessAtRef = useRef(0);
  const pendingStatusRef = useRef("");
  const formValuesRef = useRef(tripInitialValues);
  const customerTripDetailRequestIdRef = useRef(0);
  const productRatesRequestIdRef = useRef(0);
  const contractVehicleTypeRequestIdRef = useRef(0);

  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [tripCompleteModalOpen, setTripCompleteModalOpen] = useState(false);
  const [tripCloseModalOpen, setTripCloseModalOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  const [tripCloseDetailLoading, setTripCloseDetailLoading] = useState(false);
  const [tripCloseDetailData, setTripCloseDetailData] = useState(null);
  const [contractOptions, setContractOptions] = useState([]);
  const [contractVehicleTypeOptions, setContractVehicleTypeOptions] = useState([]);
  const [customerLocationOptions, setCustomerLocationOptions] = useState([]);
  const [customerProductOptions, setCustomerProductOptions] = useState([]);
  const [customerTripDetailLoading, setCustomerTripDetailLoading] = useState(false);
  const [customerProductRatesLoading, setCustomerProductRatesLoading] = useState(false);
  const [contractVehicleTypeLoading, setContractVehicleTypeLoading] = useState(false);
  const [statusModalConfig, setStatusModalConfig] = useState({
    titleText: "",
    description: "",
    showConfirmButton: true,
    confirmText: "Yes",
    cancelText: "Cancel",
  });

  const [activeTrackerMap, setActiveTrackerMap] = useState("tracker");
  const selectMenuPortalTarget = typeof document !== "undefined" ? document.body : null;

  const customerOptions = useMemo(() => {
    const normalizedOptions = resolveDropdownCollection(customerDropdownData, [
      "customer",
      "customers",
      "data",
      "results",
    ]).map((option) => normalizeSelectOption(option, "Customer"));
    const selectedCustomerOption = buildTripCustomerOption(
      tripDetailsData?.data || tripDetailsData,
      getTripCustomerId(tripDetailsData)
    );

    return mergeSelectedOption(normalizedOptions, selectedCustomerOption);
  }, [customerDropdownData, tripDetailsData]);

  const displayedContractOptions = useMemo(() => {
    const normalizedOptions = (Array.isArray(contractOptions) ? contractOptions : []).map(
      (option) => normalizeSelectOption(option, "Contract")
    );
    const selectedContractOption = buildTripContractOption(
      tripDetailsData?.data || tripDetailsData,
      getTripContractId(tripDetailsData)
    );

    return mergeSelectedOption(normalizedOptions, selectedContractOption);
  }, [contractOptions, tripDetailsData]);

  const getActiveFormValues = () => formValuesRef.current || initialValues;

  const resetProductRateOptions = () => {
    productRatesRequestIdRef.current += 1;
    setCustomerProductOptions([]);
    setCustomerProductRatesLoading(false);
  };

  const fetchCustomerProductRatesData = async (customerId, startDateTime) => {
    const normalizedCustomerId = String(customerId || "").trim();
    const productRateDate = getTripProductRateDateForApi(startDateTime);

    if (!normalizedCustomerId || !productRateDate) {
      resetProductRateOptions();
      return [];
    }

    const numericCustomerId = Number(normalizedCustomerId);
    const requestId = productRatesRequestIdRef.current + 1;
    productRatesRequestIdRef.current = requestId;
    setCustomerProductRatesLoading(true);

    try {
      const response = await apiService.getProductRatesByDate({
        customer_id: Number.isNaN(numericCustomerId)
          ? normalizedCustomerId
          : numericCustomerId,
        date: productRateDate,
      });

      if (productRatesRequestIdRef.current !== requestId) {
        return null;
      }

      const nextProductOptions = mapCustomerProductsToOptions(
        response?.data || response || []
      );
      setCustomerProductOptions(nextProductOptions);

      return nextProductOptions;
    } catch (error) {
      if (productRatesRequestIdRef.current !== requestId) {
        return null;
      }

      setCustomerProductOptions([]);
      errorNotify(error);
      return [];
    } finally {
      if (productRatesRequestIdRef.current === requestId) {
        setCustomerProductRatesLoading(false);
      }
    }
  };

  const fetchCustomerTripDetailData = async (customerId) => {
    contractVehicleTypeRequestIdRef.current += 1;
    setContractVehicleTypeOptions([]);
    setContractVehicleTypeLoading(false);

    if (!customerId) {
      customerTripDetailRequestIdRef.current += 1;
      setContractOptions([]);
      setCustomerLocationOptions([]);
      resetProductRateOptions();
      setCustomerTripDetailLoading(false);
      return null;
    }

    const requestId = customerTripDetailRequestIdRef.current + 1;
    customerTripDetailRequestIdRef.current = requestId;
    setCustomerTripDetailLoading(true);

    try {
      const response = await apiService.getCustomerTripDetail(customerId);

      if (customerTripDetailRequestIdRef.current !== requestId) {
        return null;
      }

      const customerTripDetailData = unwrapApiPayload(response);
      setContractOptions(mapCustomerContractsToOptions(customerTripDetailData?.contracts));
      setCustomerLocationOptions(
        mapCustomerLocationsToOptions(getCustomerTripLocations(customerTripDetailData))
      );

      const activeContractId =
        getActiveFormValues()?.contractId || getTripContractId(tripDetailsData) || "0";
      await fetchContractVehicleTypeOptions(activeContractId);

      return customerTripDetailData;
    } catch (error) {
      if (customerTripDetailRequestIdRef.current === requestId) {
        setContractOptions([]);
        setCustomerLocationOptions([]);
        errorNotify(error);
      }
      return null;
    } finally {
      if (customerTripDetailRequestIdRef.current === requestId) {
        setCustomerTripDetailLoading(false);
      }
    }
  };

  const fetchContractVehicleTypeOptions = async (contractId) => {
    const nextContractId = String(contractId || "").trim();

    if (!nextContractId) {
      contractVehicleTypeRequestIdRef.current += 1;
      setContractVehicleTypeOptions([]);
      setContractVehicleTypeLoading(false);
      return [];
    }

    const requestId = contractVehicleTypeRequestIdRef.current + 1;
    contractVehicleTypeRequestIdRef.current = requestId;
    setContractVehicleTypeLoading(true);

    try {
      const response = await apiService.getContractTripDetail(nextContractId);

      if (contractVehicleTypeRequestIdRef.current !== requestId) {
        return [];
      }

      const nextVehicleTypeOptions = mapContractVehicleTypesToOptions(
        response?.data?.vehicle_types
      );
      setContractVehicleTypeOptions(nextVehicleTypeOptions);
      return nextVehicleTypeOptions;
    } catch (error) {
      if (contractVehicleTypeRequestIdRef.current !== requestId) {
        return [];
      }

      setContractVehicleTypeOptions([]);
      errorNotify(error);
      return [];
    } finally {
      if (contractVehicleTypeRequestIdRef.current === requestId) {
        setContractVehicleTypeLoading(false);
      }
    }
  };

  const openStatusModal = (nextStatus, config = {}) => {
    pendingStatusRef.current = nextStatus;
    setPendingStatus(nextStatus);
    setStatusModalConfig({
      titleText: "",
      description: "",
      showConfirmButton: true,
      confirmText: "Yes",
      cancelText: "Cancel",
      ...config,
    });
    setStatusModalOpen(true);
  };

  const closeStatusModal = (shouldClearPendingStatus = true) => {
    setStatusModalOpen(false);
    setStatusModalConfig({
      titleText: "",
      description: "",
      showConfirmButton: true,
      confirmText: "Yes",
      cancelText: "Cancel",
    });

    if (shouldClearPendingStatus) {
      pendingStatusRef.current = "";
      setPendingStatus(null);
    }
  };

  const closeTripCloseModal = (shouldClearPendingStatus = true) => {
    setTripCloseModalOpen(false);
    setTripCloseDetailLoading(false);
    setTripCloseDetailData(null);

    if (shouldClearPendingStatus) {
      pendingStatusRef.current = "";
      setPendingStatus(null);
    }
  };

  const handleStatusChange = (newStatus) => {
    const normalizedStatus = normalizeTripStatusValue(newStatus);
    const currentStatus = currentStatusRef.current;
    const localStatus = normalizeTripStatusValue(status);
    const storeStatus = normalizeTripStatusValue(tripDetailsData?.status);
    const isClosedReopenCooldownActive =
      normalizedStatus === "closed" &&
      Date.now() - lastClosedModalSuccessAtRef.current < 2000;

    if (
      !normalizedStatus ||
      normalizedStatus === currentStatus ||
      normalizedStatus === localStatus ||
      normalizedStatus === storeStatus ||
      (normalizedStatus === "closed" && suppressClosedModalReopenRef.current) ||
      isClosedReopenCooldownActive ||
      normalizedStatus === pendingStatusRef.current ||
      statusModalOpen ||
      tripCloseModalOpen ||
      statusChangeLoading
    ) {
      return;
    }

    const activeValues = getActiveFormValues();
    const startDateTime = activeValues?.startDateTime || "";
    const expectedEndDateTime = activeValues?.expectedEndDateTime || "";
    const actualEndDateTime = activeValues?.actualEndDateTime || "";

    if (isEndBeforeStart(startDateTime, expectedEndDateTime)) {
      openStatusModal(normalizedStatus, {
        titleText: "Expected end date & time is invalid.",
        description: `Expected end date & time must be after ${formatLocalDateTime(startDateTime)}.`,
        showConfirmButton: false,
        cancelText: "Close",
      });
      return;
    }

    if (isEndBeforeStart(startDateTime, actualEndDateTime)) {
      openStatusModal(normalizedStatus, {
        titleText: "Actual end date & time is invalid.",
        description: `Actual end date & time must be after ${formatLocalDateTime(startDateTime)}.`,
        showConfirmButton: false,
        cancelText: "Close",
      });
      return;
    }

    if (normalizedStatus === "closed") {
      pendingStatusRef.current = normalizedStatus;
      setPendingStatus(normalizedStatus);
      setTripCloseModalOpen(true);
      setTripCloseDetailLoading(false);
      setTripCloseDetailData(null);

      // Settlement detail API is intentionally not called for now.
      // apiService
      //   .getTripSettlementDetail(id)
      //   .then((response) => {
      //     setTripCloseDetailData(response?.data || response || null);
      //   })
      //   .catch((error) => {
      //     closeTripCloseModal();
      //     errorNotify(error);
      //   })
      //   .finally(() => {
      //     setTripCloseDetailLoading(false);
      //   });
      return;
    }

    if (isTripCompletionStatus(normalizedStatus)) {
      if (!startDateTime) {
        openStatusModal(normalizedStatus, {
          titleText: `Trip cannot be marked ${formatStatusLabel(normalizedStatus)} yet.`,
          description: "Please set the start date & time first.",
          showConfirmButton: false,
          cancelText: "Close",
        });
        return;
      }

      if (!hasDateTimePassed(startDateTime)) {
        openStatusModal(normalizedStatus, {
          titleText: `Trip cannot be marked ${formatStatusLabel(normalizedStatus)} before the start date & time.`,
          description: `Trip can be marked ${formatStatusLabel(normalizedStatus)} after ${formatLocalDateTime(startDateTime)}.`,
          showConfirmButton: false,
          cancelText: "Close",
        });
        return;
      }

      if (normalizedStatus === "completed") {
        pendingStatusRef.current = normalizedStatus;
        setPendingStatus(normalizedStatus);
        setTripCompleteModalOpen(true);
        return;
      }

      const completionDateTime = activeValues?.actualEndDateTime || "";

      if (!completionDateTime) {
        openStatusModal(normalizedStatus, {
          titleText: "Actual end date & time is required.",
          description: "Please select Actual End Date & Time before completing the trip.",
          showConfirmButton: false,
          cancelText: "Close",
        });
        return;
      }

      if (isEndBeforeStart(startDateTime, completionDateTime)) {
        openStatusModal(normalizedStatus, {
          titleText: "Actual end date & time is invalid.",
          description: `Actual end date & time must be after ${formatLocalDateTime(startDateTime)}.`,
          showConfirmButton: false,
          cancelText: "Close",
        });
        return;
      }

      openStatusModal(normalizedStatus, {
        description: `Completion date & time: ${formatLocalDateTime(completionDateTime)}.`,
      });
      return;
    }

    openStatusModal(normalizedStatus);
  };

  const handleTripComplete = async (values) => {
    const activeValues = getActiveFormValues();

    if (isEndOdometerNotGreaterThanStart(activeValues?.startOdometer, values?.end_odm)) {
      errorNotify("End Odometer Reading must be greater than Start Odometer.");
      return;
    }

    if (isEndBeforeStart(activeValues?.startDateTime, values?.actual_end_at)) {
      errorNotify("Actual End Date & Time cannot be earlier than Start Date & Time.");
      return;
    }

    setStatusChangeLoading(true);
    setTripCompleteModalOpen(false);

    try {
      const payload = {
        trip_id: Number(id),
        end_odm: parseNumberOrZero(values.end_odm),
        closing_fuel: parseNumberOrZero(values.closing_fuel),
        shortage_qty: parseNumberOrZero(values.shortage_qty),
        relief_qty: parseNumberOrZero(values.relief_qty),
        delivered_qty: parseNumberOrZero(values.delivered_qty),
        actual_end_at: formatUtcDateTimeForApi(values.actual_end_at),
      };

      await dispatch(actualTripComplete(payload)).unwrap();

      await dispatch(getTripDetails(id)).unwrap();
      await dispatch(fetchTripTimeline(id)).unwrap();

      currentStatusRef.current = "completed";
      setStatus("completed");

      successNotify("Trip completed successfully.");
    } catch (error) {
      errorNotify(error);
    } finally {
      setStatusChangeLoading(false);
      pendingStatusRef.current = "";
      setPendingStatus(null);
    }
  };

  const handleTripClose = async () => {
    setStatusChangeLoading(true);

    try {
      await apiService.tripClosed({
        trip_id: Number(id),
      });

      // Set suppress refs and close modal immediately to prevent timing issues on re-render
      suppressClosedModalReopenRef.current = true;
      lastClosedModalSuccessAtRef.current = Date.now();
      currentStatusRef.current = "closed";
      setStatus("closed");
      closeTripCloseModal();

      // Dispatch updates in the background
      await dispatch(getTripDetails(id)).unwrap();
      await dispatch(fetchTripTimeline(id)).unwrap();
      successNotify("Trip closed successfully.");
    } catch (error) {
      suppressClosedModalReopenRef.current = false;
      lastClosedModalSuccessAtRef.current = 0;
      errorNotify(error);
    } finally {
      pendingStatusRef.current = "";
      setPendingStatus(null);
      setStatusChangeLoading(false);
    }
  };

  const handleConfirmStatusChange = async () => {
    const nextStatus = normalizeTripStatusValue(
      pendingStatusRef.current || pendingStatus
    );

    if (!nextStatus) {
      closeStatusModal();
      return;
    }

    const activeValues = getActiveFormValues();
    const startDateTime = activeValues?.startDateTime || "";
    const isCompletionFlow = isTripCompletionStatus(nextStatus);
    const completionDateTime = isCompletionFlow
      ? (activeValues?.actualEndDateTime || "")
      : "";

    if (isCompletionFlow && !startDateTime) {
      errorNotify("Start date & time is required before completing the trip.");
      closeStatusModal();
      return;
    }

    if (isCompletionFlow && !hasDateTimePassed(startDateTime)) {
      errorNotify(`Trip can be completed after ${formatLocalDateTime(startDateTime)}.`);
      closeStatusModal();
      return;
    }

    if (isCompletionFlow && !completionDateTime) {
      errorNotify("Actual End Date & Time is required before completing the trip.");
      closeStatusModal();
      return;
    }

    if (isCompletionFlow && isEndBeforeStart(startDateTime, completionDateTime)) {
      errorNotify("Actual end date & time must be after the start date & time.");
      closeStatusModal();
      return;
    }

    setStatusChangeLoading(true);
    closeStatusModal(false);

    try {
      const statusPayload = {
        trip_id: id,
        status: nextStatus,
      };

      if (isCompletionFlow) {
        const formattedCompletionDateTime = formatUtcDateTimeForApi(completionDateTime);
        statusPayload.actual_end_at = formattedCompletionDateTime;
        statusPayload.actual_at = formattedCompletionDateTime;
      }

      await dispatch(changeTripStatus(statusPayload)).unwrap();

      let completionSyncError = "";

      if (isCompletionFlow) {
        const syncedValues = {
          ...initialValues,
          actualEndDateTime: completionDateTime,
        };

        const completionPayload = buildTripPayload(
          syncedValues,
          id,
          currentStatusRef.current,
          customerLocationOptions,
          customerProductOptions
        );

        completionPayload.updatedKeys = ["actual_end_at", "actual_at"];

        try {
          await dispatch(tripUpdate({ data: completionPayload })).unwrap();
        } catch (syncError) {
          completionSyncError =
            syncError ||
            "Trip status updated, but completion date & time could not be synced.";
        }
      }

      await dispatch(getTripDetails(id)).unwrap();
      await dispatch(fetchTripTimeline(id)).unwrap();
      currentStatusRef.current = nextStatus;
      setStatus(nextStatus);

      if (completionSyncError) {
        errorNotify(completionSyncError);
      } else {
        successNotify("Trip status updated successfully.");
      }
    } catch (error) {
      errorNotify(error);
    } finally {
      pendingStatusRef.current = "";
      setPendingStatus(null);
      setStatusChangeLoading(false);
      closeStatusModal();
    }
  };

  const userOptions = useMemo(() => {
    const baseOptions = (Array.isArray(usersDropdownData) ? usersDropdownData : []).map((user) => ({
      value: String(user?.value ?? "").trim(),
      label: user?.label,
      image: user?.image,
      showIcon: true,
    }));

    return mergeTripUserOptions(baseOptions, tripDetailsData?.data || tripDetailsData);
  }, [tripDetailsData, usersDropdownData]);

  useEffect(() => {
    dispatch(getTripDetails(id));
    dispatch(fetchTripTimeline(id));
    dispatch(setSelectedTripId(id));
    dispatch(setViewAllTrips(false));
    dispatch(fetchCustomerDropdown());
    dispatch(fetchCostCenterDropdown());
    dispatch(fetchVehiclesTrackingPlusDropdown());
    dispatch(fetchUsersDropdown());
    dispatch(fetchBanksDropdown());
    dispatch(fetchProductsDropdown());
  }, [id, dispatch]);

  useEffect(() => {
    if (tripDetailsData) {
      const d = tripDetailsData?.data || tripDetailsData;
      const stopsFromApi = (Array.isArray(d.stops) ? [...d.stops] : [])
        .sort((a, b) => (Number(a?.stop_number) || 0) - (Number(b?.stop_number) || 0))
        .map(normalizeTripStop);

      const normalizedStops =
        stopsFromApi.length >= 2
          ? stopsFromApi
          : [...stopsFromApi, ...Array.from({ length: 2 - stopsFromApi.length }, () => getBlankStop())];

      const nextStatus = normalizeTripStatusValue(d.status);
      if (nextStatus !== "closed") {
        suppressClosedModalReopenRef.current = false;
        lastClosedModalSuccessAtRef.current = 0;
      }
      currentStatusRef.current = nextStatus;
      setStatus(nextStatus);

      setInitialValues({
        ...tripInitialValues,
        tripId: d.trip_id,
        description: d.description || "",
        notes: d.note || "",
        customer: getTripCustomerId(d),
        contractId: getTripContractId(d),
        vehicleType: String(
          getFirstDefinedValue([
            d.vehicle_type_id,
            d.vehicleType,
            d.vehicle_type?.id,
          ], "")
        ).trim(),
        costCenter: d.cost_center_id || "",
        loadType: d.load_type || "",
        stops: normalizedStops,
        product: "",
        quantity: "",
        dispatch_location: {
          dispatch_point: d.dispatch_point,
          dispatch_latitude: d.dispatch_latitude,
          dispatch_longitude: d.dispatch_longitude,
        },
        decanting_location: {
          decanting_point: d.decanting_point,
          decanting_latitude: d.decanting_latitude,
          decanting_longitude: d.decanting_longitude,
        },
        startDateTime: formatUtcDateTimeForInput(d.start_at),
        expectedEndDateTime: formatUtcDateTimeForInput(d.expected_end_at),
        actualEndDateTime: formatUtcDateTimeForInput(d.actual_end_at),

        startOdometer: getNumericInputValue(d.start_odm),
        endOdometer: getNumericInputValue(d.end_odm),
        totalReading: getNumericInputValue(d.total_odm),

        openingFuel: getNumericInputValue(d.vehicle?.current_fuel),
        allocatedPettyCash: getNumericInputValue(d.allocated_petty_cash),
        total_filled_fuel: getNumericInputValue(d.total_filled_fuel),
        shortage_qty: getNumericInputValue(d.shortage_qty),
        relief_qty: getNumericInputValue(d.relief_qty),

        allocatedFuel: getNumericInputValue(d.allocated_fuel),
        closing_fuel: getNumericInputValue(d.closing_fuel),

        firstDriver: d.primary_driver_id || "",
        firstDriverContact: normalizePhoneNumberValue(d.primary_driver_contact),
        supplementaryDriver: d.secondary_driver_id || "",
        supplementaryDriverContact: normalizePhoneNumberValue(d.secondary_driver_contact),
        helper: d.helper_id || "",
        helperContact: normalizePhoneNumberValue(d.helper_contact),

        vehicle: d.vehicle_id || "",
        registrationNumber: d.vehicle?.registration_number || "",
        uploadedBy: d.created_by || "",
        uploadDate: d.created_at ? d.created_at.split("T")[0] : "",

        billingFrequency: d.billing_frequency || "",
        paymentMethod: d.payment_method || "",
        paymentTerms: d.payment_term || "",
        bank: d.bank_name || "",
        accountTitle: d.account_title || "",
        iban: d.iban_number || "",
      });
    }
  }, [tripDetailsData]);

  useEffect(() => {
    const detailData = tripDetailsData?.data || tripDetailsData;
    const customerId = getTripCustomerId(detailData);
    const startDateTime = formatUtcDateTimeForInput(detailData?.start_at);

    if (customerId) {
      fetchCustomerTripDetailData(customerId);
      fetchCustomerProductRatesData(customerId, startDateTime).then((nextProductOptions) => {
        if (!Array.isArray(nextProductOptions)) {
          return;
        }

        setInitialValues((previousValues) => {
          if (
            String(previousValues?.customer || "") !== String(customerId) ||
            String(previousValues?.startDateTime || "") !== String(startDateTime)
          ) {
            return previousValues;
          }

          const nextStops = syncStopsWithProductRateOptions(
            previousValues?.stops,
            nextProductOptions
          );

          if (_.isEqual(nextStops, previousValues?.stops)) {
            return previousValues;
          }

          return {
            ...previousValues,
            stops: nextStops,
          };
        });
      });
      return;
    }

    setContractOptions([]);
    setCustomerLocationOptions([]);
    resetProductRateOptions();
  }, [tripDetailsData]);

  useEffect(() => {
    if (!initialValues?.customer) {
      contractVehicleTypeRequestIdRef.current += 1;
      setContractVehicleTypeOptions([]);
      setContractVehicleTypeLoading(false);
      return;
    }

    fetchContractVehicleTypeOptions(initialValues?.contractId || "0");
  }, [initialValues?.customer, initialValues?.contractId]);

  const tripsTabs = [
    {
      key: "detail",
      label: "Details",
      icon: activeTab === "detail" ? activeDetailsIcon : detailsIcon,
    },
    {
      key: "history",
      label: "History",
      icon: activeTab === "history" ? activeHistoryIcon : historyIcon,
    },
    {
      key: "violations",
      label: "Violations",
      icon: activeTab === "violations" ? activeViolationsIcon : violationsIcon,
    },
    {
      key: "inspection",
      label: "Inspection",
      icon: activeTab === "inspection" ? activeInspectionIcon : inspectionIcon,
    },
  ];

  const calculateTripTotalExpenses = useMemo(() => {
    const fuelCost = Number(tripDetailsData?.fuelCost || 0);
    const driverExpenses = Number(tripDetailsData?.total_trip_driver_expenses?.total_expense || 0);
    return fuelCost + driverExpenses;
  }, [tripDetailsData]);

  const totalPettyCashAmount = useMemo(() => {
    return (tripDetailsData?.trip_petty_cash || []).reduce(
      (sum, item) => sum + Number(item?.amount || 0),
      0
    );
  }, [tripDetailsData]);

  const totalTripExpenseAmount = useMemo(() => {
    return (tripDetailsData?.trip_expenses || []).reduce(
      (sum, item) => sum + Number(item?.amount || 0),
      0
    );
  }, [tripDetailsData]);

  const remainingPettyCashAmount = Math.max(
    totalPettyCashAmount - totalTripExpenseAmount,
    0
  );

  if (loading && !tripDetailsData?.data) {
    return <SpinnerLoader />;
  }

  return (
    <>
      <Breadcrumbs values={["Trip Management", "Trip Details"]} />

      <Formik
        initialValues={initialValues}
        validationSchema={tripDetailSchema}
        enableReinitialize={true}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            if (isEndBeforeStart(values.startDateTime, values.expectedEndDateTime)) {
              errorNotify("Expected End Date & Time cannot be earlier than Start Date & Time.");
              setSubmitting(false);
              return;
            }

            if (isEndBeforeStart(values.startDateTime, values.actualEndDateTime)) {
              errorNotify("Actual End Date & Time cannot be earlier than Start Date & Time.");
              setSubmitting(false);
              return;
            }

            if (isEndOdometerNotGreaterThanStart(values.startOdometer, values.endOdometer)) {
              errorNotify("End Odometer Reading must be greater than Start Odometer.");
              setSubmitting(false);
              return;
            }

            if (
              isTripCompletionStatus(status) &&
              !hasDateTimePassed(values.startDateTime)
            ) {
              errorNotify(`Trip can be completed after ${formatLocalDateTime(values.startDateTime)}.`);
              setSubmitting(false);
              return;
            }

            const currentPayload = buildTripPayload(values, id, status, customerLocationOptions, customerProductOptions);
            const initialPayload = buildTripPayload(initialValues, id, status, customerLocationOptions, customerProductOptions);

            const changedKeys = Object.keys(currentPayload).filter((key) => {
              return !_.isEqual(currentPayload[key], initialPayload[key]);
            });

            const payload = {
              ...currentPayload,
              updatedKeys: changedKeys,
            };

            await dispatch(tripUpdate({ data: payload })).unwrap();
            successNotify("Trip details updated successfully.");
            dispatch(getTripDetails(id));
          } catch (error) {
            errorNotify(error);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
          handleSubmit,
          isSubmitting,
          submitCount,
        }) => {
          formValuesRef.current = values;
          const shouldUseCustomerProductRates = Boolean(values.customer && values.startDateTime);
          const currentProductOptions = shouldUseCustomerProductRates
            ? customerProductOptions
            : normalizedProductsDropdownData;

          const syncProductRatesForForm = async (
            customerId,
            startDateTime,
            stopsToSync = values.stops
          ) => {
            const hasProductRateFilters = Boolean(customerId && startDateTime);

            if (!hasProductRateFilters) {
              resetProductRateOptions();
              setFieldValue(
                "stops",
                syncStopsWithProductRateOptions(stopsToSync, [], {
                  preserveMissingProduct: false,
                }),
                false
              );
              return [];
            }

            const nextProductOptions = await fetchCustomerProductRatesData(
              customerId,
              startDateTime
            );

            if (!Array.isArray(nextProductOptions)) {
              return null;
            }

            setFieldValue(
              "stops",
              syncStopsWithProductRateOptions(stopsToSync, nextProductOptions),
              false
            );

            return nextProductOptions;
          };

          const handleCustomerChange = async (selectedCustomer) => {
            const nextCustomerValue = selectedCustomer?.value || "";
            const nextStops = buildMinimumStops();

            setFieldValue("customer", nextCustomerValue, false);
            setFieldValue("contractId", nextCustomerValue ? "0" : "", false);
            setFieldValue("vehicleType", "", false);
            setFieldValue("stops", nextStops, false);
            setFieldTouched("customer", false, false);
            setFieldTouched("contractId", false, false);
            setFieldTouched("vehicleType", false, false);
            resetProductRateOptions();

            await fetchCustomerTripDetailData(nextCustomerValue);
            await syncProductRatesForForm(
              nextCustomerValue,
              values.startDateTime,
              nextStops
            );
          };

          const handleStartDateTimeChange = async (event) => {
            const nextStartDateTime = event?.target?.value || "";

            setFieldValue("startDateTime", nextStartDateTime);
            await syncProductRatesForForm(
              values.customer,
              nextStartDateTime,
              values.stops
            );
          };

          const handleContractChange = async (selectedContract) => {
            const nextContractId = selectedContract?.value || "";

            setFieldValue("contractId", nextContractId, false);
            setFieldValue("vehicleType", "", false);
            setFieldTouched("contractId", false, false);
            setFieldTouched("vehicleType", false, false);
            setContractVehicleTypeOptions([]);

            const nextVehicleTypeOptions = await fetchContractVehicleTypeOptions(nextContractId);
            setFieldValue(
              "vehicleType",
              nextVehicleTypeOptions?.[0]?.value ? String(nextVehicleTypeOptions[0].value) : "",
              false
            );
          };

          const normalizedStatus = normalizeTripStatusValue(status);
          const canReorderStops = normalizedStatus === "new";
          const isTripCompleted = normalizedStatus === "completed";
          const isStatusLocked = normalizedStatus === "closed";
          const statusOptions =
            normalizedStatus === "new"
              ? [{ label: "In Transit", value: "in_transit" }]
              : normalizedStatus === "in_transit"
                ? [
                  { label: "Driver Completed", value: "driver_completed" },
                  { label: "Completed", value: "completed" },
                ]
                : normalizedStatus === "driver_completed"
                  ? [{ label: "Completed", value: "completed" }]
                  : normalizedStatus === "completed"
                    ? [{ label: "Closed", value: "closed" }]
                    : [];
          const currentStatusOption = normalizedStatus
            ? {
              label: formatStatusLabel(normalizedStatus),
              value: normalizedStatus,
            }
            : null;
          const disableTripDetailsSave = isSubmitting || isTripCompleted;

          const tripStartAt = (tripDetailsData?.data || tripDetailsData)?.start_at;
          const isBeforeTripStart = tripStartAt ? new Date() < new Date(tripStartAt) : false;
          const disablePettyCashActions = isTripCompleted || isBeforeTripStart;
          const pettyCashLockReason = isTripCompleted
            ? "Petty cash cannot be added or edited after trip completion."
            : isBeforeTripStart
            ? "Petty cash cannot be added or edited before the trip start date & time."
            : "";

          const hasPettyCash = totalPettyCashAmount > 0;
          const canManageTripExpenses = hasPettyCash && remainingPettyCashAmount > 0 && normalizedStatus !== "new";
          const tripUserSource = tripDetailsData?.data || tripDetailsData || {};
          const firstDriverOption =
            findTripUserOption(userOptions, values.firstDriver) ||
            buildTripUserOption(tripUserSource?.primary_driver, values.firstDriver);
          const supplementaryDriverOption =
            findTripUserOption(userOptions, values.supplementaryDriver) ||
            buildTripUserOption(tripUserSource?.secondary_driver, values.supplementaryDriver);
          const helperOption =
            findTripUserOption(userOptions, values.helper) ||
            buildTripUserOption(tripUserSource?.helper, values.helper);

          let tripExpensesLockReason = "";
          if (normalizedStatus === "new") {
            tripExpensesLockReason = "Expenses cannot be added until the trip has been started.";
          } else if (!hasPettyCash) {
            tripExpensesLockReason = "Trip Expenses can only be added when petty cash exists.";
          } else if (remainingPettyCashAmount <= 0) {
            tripExpensesLockReason = "Trip Expenses limit reached. Petty cash amount has been fully used.";
          }

          const isEndOdometerInvalid = isEndOdometerNotGreaterThanStart(
            values.startOdometer,
            values.endOdometer
          );
          const endOdometerError = isEndOdometerInvalid
            ? "End Odometer Reading must be greater than Start Odometer"
            : errors.endOdometer;
          const showEndOdometerError = submitCount > 0;

          return (
            <>
              <div className="page_header">
                <h2>Trip Details</h2>

                <div className="d-flex align-items-center gap-2">
                  <div className="status_select">
                    <span className="select_icon">
                      {statusIcon}
                    </span>
                    <ReactSelect
                      label=""
                      isSearchable={false}
                      placeholder="Select Status"
                      options={statusOptions}
                      loading={loading}
                      name="status"
                      onChange={(opt) => handleStatusChange(opt ? opt.value : "")}
                      isDisabled={loading || statusChangeLoading || isStatusLocked}
                      isSearch={false}
                      isLoading={loading}
                      styles={tripStyles}
                      value={currentStatusOption}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  <Button
                    cancelBtn={() => {
                      navigate(-1);
                    }}
                    loading={isSubmitting}
                    disabled={disableTripDetailsSave}
                    submitText={isSubmitting ? "Saving..." : "Save Details"}
                    type="button"
                    shouldRow={false}
                    isShowSubmitBtn={true}
                    submitBtn={handleSubmit}
                  />
                </div>
              </div>

              <div className="page_container">
                <Row>
                  <Col md={9}>
                    <div className="trip_details_main_div">
                      <div>
                        <h6>
                          Trip ID: <span>{values.tripId || "N/A"}</span>
                        </h6>
                      </div>
                      <CustomTabs
                        tabs={tripsTabs}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                      />
                    </div>

                    {activeTab === "detail" && (
                      <>
                        <div className="content_box">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0">Trip Information</h6>
                            {normalizeTripStatusValue(status) === "closed" && (
                              <button
                                type="button"
                                className="main_btn d-flex align-items-center gap-1"
                                onClick={handleDownloadCloserReport}
                                disabled={downloadingReport}
                                style={{ height: "35px", padding: "5px 12px", fontSize: "13px" }}
                              >
                                <FiDownload style={{ fontSize: "16px" }} />
                                {downloadingReport ? "Downloading..." : "Download Report"}
                              </button>
                            )}
                          </div>
                          <p>Main information for this trip record.</p>

                          <Row>
                            <Col md={4}>
                              <Select
                                label="Vehicle"
                                placeholder="Select Vehicle"
                                options={vehiclesTrackingPlusDropdownData}
                                loading={loading}
                                name="vehicle"
                                value={
                                  vehiclesTrackingPlusDropdownData?.find(
                                    (o) => String(o.value) === String(values.vehicle)
                                  ) || null
                                }
                                onChange={(opt) =>
                                  setFieldValue("vehicle", opt ? opt.value : "")
                                }
                                onBlur={handleBlur}
                                error={errors.vehicle}
                                touched={touched.vehicle}
                                isSearch={false}
                                isDisabled={true}
                              />
                            </Col>

                            <Col md={4}>
                              <Select
                                label="Customer"
                                placeholder="Select Customer"
                                options={customerOptions}
                                loading={loading}
                                name="customer"
                                menuPortalTarget={selectMenuPortalTarget}
                                value={findSelectedOption(customerOptions, values.customer)}
                                onChange={handleCustomerChange}
                                onBlur={handleBlur}
                                error={errors.customer}
                                touched={touched.customer}
                                isSearch={true}
                              />
                            </Col>

                            <Col md={4}>
                              <Select
                                label="Contract"
                                placeholder={values.customer ? "Select Contract" : "Select Customer First"}
                                options={displayedContractOptions}
                                loading={customerTripDetailLoading}
                                name="contractId"
                                menuPortalTarget={selectMenuPortalTarget}
                                value={findSelectedOption(displayedContractOptions, values.contractId)}
                                onChange={handleContractChange}
                                onBlur={handleBlur}
                                error={errors.contractId}
                                touched={touched.contractId}
                                isSearch={true}
                                isDisabled={!values.customer || customerTripDetailLoading}
                              />
                            </Col>

                            <Col md={4}>
                              <Select
                                label="Vehicle Type"
                                placeholder={values.customer ? "Select Vehicle Type" : "Select Customer First"}
                                options={contractVehicleTypeOptions}
                                loading={contractVehicleTypeLoading}
                                name="vehicleType"
                                menuPortalTarget={selectMenuPortalTarget}
                                value={
                                  contractVehicleTypeOptions?.find(
                                    (o) => String(o.value) === String(values.vehicleType)
                                  ) || null
                                }
                                onChange={(opt) =>
                                  setFieldValue("vehicleType", opt ? opt.value : "")
                                }
                                onBlur={handleBlur}
                                error={errors.vehicleType}
                                touched={touched.vehicleType}
                                isSearch={true}
                                isDisabled={!values.customer || contractVehicleTypeLoading}
                              />
                            </Col>

                            <Col md={4}>
                              <Select
                                label="Cost Center"
                                placeholder="Select Cost Center"
                                options={costCenterDropdownData}
                                loading={loading}
                                name="costCenter"
                                value={
                                  costCenterDropdownData?.find(
                                    (o) => String(o.value) === String(values.costCenter)
                                  ) || null
                                }
                                onChange={(opt) =>
                                  setFieldValue("costCenter", opt ? opt.value : "")
                                }
                                onBlur={handleBlur}
                                error={errors.costCenter}
                                touched={touched.costCenter}
                                isSearch={true}
                              />
                            </Col>

                            <Col md={4}>
                              <Input
                                label="Start Date & Time"
                                type="datetime-local"
                                name="startDateTime"
                                value={values.startDateTime}
                                onChange={handleStartDateTimeChange}
                                onBlur={handleBlur}
                                error={errors.startDateTime}
                                touched={touched.startDateTime}
                              />
                            </Col>

                            <Col md={4}>
                              <Input
                                label="Expected End Date & Time"
                                type="datetime-local"
                                name="expectedEndDateTime"
                                value={values.expectedEndDateTime}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.expectedEndDateTime}
                                touched={touched.expectedEndDateTime}
                              />
                            </Col>

                            <Col md={4}>
                              <Input
                                label="Actual End Date & Time"
                                type="datetime-local"
                                name="actualEndDateTime"
                                value={values.actualEndDateTime}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.actualEndDateTime}
                                touched={touched.actualEndDateTime}
                              />
                            </Col>

                            <Col md={4}>
                              <Input
                                label="Start Odometer Reading"
                                type="text"
                                placeholder="00"
                                name="startOdometer"
                                value={values.startOdometer}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.startOdometer}
                                touched={touched.startOdometer}
                                disabled={normalizedStatus !== "in_transit"}
                              />
                            </Col>

                            <Col md={4}>
                              <Input
                                label="End Odometer Reading"
                                type="text"
                                placeholder="00"
                                name="endOdometer"
                                value={values.endOdometer}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={endOdometerError}
                                touched={showEndOdometerError}
                                disabled={false}
                              />
                            </Col>

                            <Col md={4}>
                              <Input
                                label="Total Reading"
                                type="text"
                                placeholder="00"
                                name="totalReading"
                                value={values.totalReading}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.totalReading}
                                touched={touched.totalReading}
                                disabled={true}
                              />
                            </Col>

                            <Col md={4}>
                              <Input
                                label="Shortage Qty"
                                type="text"
                                placeholder="0"
                                name="shortage_qty"
                                value={values.shortage_qty}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.shortage_qty}
                                touched={touched.shortage_qty}
                                disabled={true}
                                required={false}
                              />
                            </Col>
                          </Row>
                        </div>

                        <div className="content_box">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h6>Stops</h6>
                              <p>Manage trip stops for this trip record.</p>
                            </div>
                          </div>

                          <Row>
                            <Col md={12}>
                              <List
                                values={values.stops || []}
                                disabled={!canReorderStops}
                                lockVertically={true}
                                onChange={async ({ oldIndex, newIndex }) => {
                                  if (!canReorderStops) return;
                                  const updatedStopsArr = arrayMove(values.stops || [], oldIndex, newIndex);
                                  setFieldValue("stops", updatedStopsArr);

                                  const payloadStops = updatedStopsArr
                                    .filter((stop) => stop.id !== null)
                                    .map((stop, i) => ({
                                      id: stop.id,
                                      stop_number: i + 1
                                    }));

                                  if (payloadStops.length > 0) {
                                    try {
                                      await dispatch(changeStopOrders({
                                        trip_id: Number(id),
                                        trip_stops: payloadStops
                                      })).unwrap();
                                      successNotify("Stop order updated successfully");
                                    } catch (err) {
                                      errorNotify(err || "Failed to update stop order");
                                    }
                                  }
                                }}
                                renderList={({ children, props }) => (
                                  <div ref={props.ref} style={props.style}>
                                    {children}
                                  </div>
                                )}
                                renderItem={({ value: stop, index, props, isDragged }) => {
                                  const {
                                    key: itemKey,
                                    ref: itemRef,
                                    style: itemStyle,
                                    tabIndex,
                                    ...itemProps
                                  } = props;

                                  const isProductDetailsRequired = shouldRequireStopProductDetails(
                                    stop?.stopType
                                  );
                                  const stopLocationOptions = getAvailableLocationOptions(
                                    index,
                                    values.stops,
                                    customerLocationOptions
                                  );
                                  const selectedLocationOption = findLocationOption(
                                    stop,
                                    stopLocationOptions
                                  );
                                  const selectedProductOption = findProductOption(
                                    stop?.product,
                                    currentProductOptions
                                  );
                                  const resolvedProductValue = String(
                                    getFirstDefinedValue(
                                      [
                                        selectedProductOption?.productValue,
                                        stop?.productValue,
                                      ],
                                      ""
                                    )
                                  ).trim();

                                  return (
                                    <div
                                      key={itemKey}
                                      ref={itemRef}
                                      tabIndex={tabIndex}
                                      {...itemProps}
                                      draggable={false}
                                      onDragStart={(e) => e.preventDefault()}
                                      style={{
                                        ...itemStyle,
                                        marginBottom: "12px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          border: "1px solid #E1E8F5",
                                          borderRadius: "12px",
                                          padding: "14px",
                                          background: isDragged ? "#F8FAFF" : "#FFFFFF",
                                          boxShadow: isDragged ? "0 8px 18px rgba(20, 71, 177, 0.12)" : "none",
                                        }}
                                      >
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                          <div className="d-flex align-items-center gap-2">
                                            <button
                                              type="button"
                                              title="Drag to Reorder"
                                              {...(canReorderStops ? { "data-movable-handle": true } : {})}
                                              tabIndex={-1}
                                              onClick={(e) => e.stopPropagation()}
                                              style={{
                                                border: "none",
                                                background: "transparent",
                                                padding: 0,
                                                lineHeight: 1,
                                                color: canReorderStops ? "#6B7280" : "#BCC8DE",
                                                cursor: canReorderStops ? "grab" : "not-allowed",
                                                userSelect: "none",
                                              }}
                                            >
                                              <FaBars size={14} />
                                            </button>
                                            <div style={{ fontSize: "14px", fontWeight: 600, color: "#3D3F5C" }}>
                                              Stop {index + 1}
                                            </div>
                                          </div>

                                          <button
                                            type="button"
                                            title="Remove Stop"
                                            disabled={(values.stops || []).length <= 2}
                                            onClick={() => {
                                              if ((values.stops || []).length <= 2) return;
                                              const updatedStops = (values.stops || []).filter((_, stopIndex) => stopIndex !== index);
                                              setFieldValue("stops", updatedStops);
                                            }}
                                            style={{
                                              border: "none",
                                              background: "transparent",
                                              padding: 0,
                                              lineHeight: 1,
                                              color: (values.stops || []).length <= 2 ? "#BCC8DE" : "#D64545",
                                              cursor: (values.stops || []).length <= 2 ? "not-allowed" : "pointer",
                                            }}
                                          >
                                            <RiDeleteBinLine size={18} />
                                          </button>
                                        </div>

                                        <Row>
                                          <Col md={12}>
                                            <Select
                                              label="Location"
                                              placeholder={values.customer ? "Select Location" : "Select Customer First"}
                                              name={`stops[${index}].location`}
                                              options={stopLocationOptions}
                                              loading={customerTripDetailLoading}
                                              menuPortalTarget={selectMenuPortalTarget}
                                              value={selectedLocationOption}
                                              onChange={(opt) =>
                                                setFieldValue(
                                                  `stops[${index}].location`,
                                                  opt ? buildLocationValueFromOption(opt) : getBlankStop().location
                                                )
                                              }
                                              onBlur={handleBlur}
                                              error={errors?.stops?.[index]?.location?.location_point}
                                              touched={touched?.stops?.[index]?.location?.location_point}
                                              isSearch={true}
                                              isDisabled={!values.customer || customerTripDetailLoading}
                                            />
                                          </Col>

                                          <Col md={6}>
                                            <Select
                                              label="Stop Type"
                                              placeholder="Select Stop Type"
                                              options={stopTypeOptions}
                                              loading={false}
                                              menuPortalTarget={selectMenuPortalTarget}
                                              name={`stops[${index}].stopType`}
                                              value={(stopTypeOptions.find((o) => o.value === stop?.stopType) || null)}
                                              onChange={(opt) => applyStopTypeDependencies(index, opt ? opt.value : "", setFieldValue, setFieldTouched)}
                                              onBlur={handleBlur}
                                              error={errors?.stops?.[index]?.stopType}
                                              touched={touched?.stops?.[index]?.stopType}
                                              isSearch={false}
                                            />
                                          </Col>

                                          <Col md={6}>
                                            <Select
                                              label="Load Type"
                                              placeholder="Select Load Type"
                                              options={stopLoadTypeOptions}
                                              loading={false}
                                              menuPortalTarget={selectMenuPortalTarget}
                                              name={`stops[${index}].loadType`}
                                              value={(stopLoadTypeOptions.find((o) => o.value === stop?.loadType) || null)}
                                              onChange={(opt) => setFieldValue(`stops[${index}].loadType`, opt ? opt.value : "")}
                                              onBlur={handleBlur}
                                              error={errors?.stops?.[index]?.loadType}
                                              touched={touched?.stops?.[index]?.loadType}
                                              isSearch={false}
                                              required={isProductDetailsRequired}
                                            />
                                          </Col>

                                          <Col md={6}>
                                            <Select
                                              label="Product"
                                              placeholder={
                                                values.customer
                                                  ? values.startDateTime
                                                    ? "Select Product"
                                                    : "Select Start Date First"
                                                  : "Select Customer First"
                                              }
                                              options={currentProductOptions}
                                              loading={customerTripDetailLoading || customerProductRatesLoading || loading}
                                              menuPortalTarget={selectMenuPortalTarget}
                                              value={selectedProductOption}
                                              name={`stops[${index}].product`}
                                              onChange={(opt) => {
                                                setFieldValue(
                                                  `stops[${index}].product`,
                                                  opt
                                                    ? String(
                                                      opt.value ??
                                                      opt.id ??
                                                      opt.product_id ??
                                                      opt.product ??
                                                      opt.label ??
                                                      ""
                                                    )
                                                    : ""
                                                );
                                                setFieldValue(
                                                  `stops[${index}].productValue`,
                                                  opt ? String(opt.productValue || "") : ""
                                                );
                                              }}
                                              onBlur={handleBlur}
                                              error={errors?.stops?.[index]?.product}
                                              touched={touched?.stops?.[index]?.product}
                                              isSearch={true}
                                              required={isProductDetailsRequired}
                                              isDisabled={!values.customer || !values.startDateTime || customerTripDetailLoading || customerProductRatesLoading}
                                            />
                                          </Col>

                                          <Col md={6}>
                                            <Input
                                              label="Product Value"
                                              type="text"
                                              placeholder="0.00"
                                              name={`stops[${index}].productValue`}
                                              value={resolvedProductValue}
                                              onChange={handleChange}
                                              onBlur={handleBlur}
                                              disabled={true}
                                              required={false}
                                            />
                                          </Col>

                                          <Col md={6}>
                                            <Input
                                              label="Product Quantity"
                                              type="number"
                                              placeholder="00"
                                              name={`stops[${index}].quantity`}
                                              value={stop?.quantity}
                                              onChange={handleChange}
                                              onBlur={handleBlur}
                                              error={errors?.stops?.[index]?.quantity}
                                              touched={touched?.stops?.[index]?.quantity}
                                              required={isProductDetailsRequired}
                                            />
                                          </Col>
                                        </Row>
                                      </div>
                                    </div>
                                  );
                                }}
                              />
                            </Col>

                            {typeof errors?.stops === "string" && (
                              <Col md={12}>
                                <div style={{ color: "#ff0000de", fontSize: "12px", marginTop: "3px", marginLeft: "5px" }}>
                                  {errors.stops}
                                </div>
                              </Col>
                            )}

                            <Col md={12}>
                              <div className="d-flex justify-content-end mb-2">
                                <button
                                  type="button"
                                  className="main_btn"
                                  onClick={() => setFieldValue("stops", [...(values.stops || []), getBlankStop()])}
                                >
                                  + Add Stop
                                </button>
                              </div>
                            </Col>
                          </Row>
                        </div>

                        <div className="content_box">
                          <div className="d-flex justify-content-between">
                            <div>
                              <h6>Tracker and Map</h6>
                              <p>Information of Tracker and Map</p>
                            </div>
                            <div className="d-flex align-items-start">
                              <button
                                type="button"
                                className={activeTrackerMap === "tracker" ? "tracker_active" : "tracker"}
                                onClick={() => setActiveTrackerMap("tracker")}
                              >
                                {activeTrackerMap === "tracker" ? trackerActiveIcon : trackerIcon} Tracker
                              </button>
                              <button
                                type="button"
                                className={activeTrackerMap === "map" ? "map_active" : "map"}
                                onClick={() => setActiveTrackerMap("map")}
                              >
                                {activeTrackerMap === "map" ? mapViewActiveIcon : mapViewIcon} Map View
                              </button>
                            </div>
                          </div>

                          {activeTrackerMap === "tracker" && <TripTracker />}
                          {activeTrackerMap === "map" && (
                            <div style={{ width: "100%", height: "600px", marginTop: "12px" }}>
                              <TripMap detailView={true} />
                            </div>
                          )}
                        </div>

                        <div className="content_box">
                          <h6>Fuel Management</h6>
                          <p>Main information for this trip record.</p>

                          <Row>
                            <Col md={6}>
                              <Input
                                label="Opening Fuel (ltrs.)"
                                type="text"
                                placeholder="00"
                                name="openingFuel"
                                value={values.openingFuel}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.openingFuel}
                                touched={touched.openingFuel}
                                disabled
                              />
                            </Col>

                            <Col md={6}>
                              <Input
                                label="Allocated Fuel (ltrs.)"
                                type="text"
                                placeholder="00"
                                name="allocatedFuel"
                                value={values.allocatedFuel}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.allocatedFuel}
                                touched={touched.allocatedFuel}
                                disabled
                              />
                            </Col>

                            <Col md={6}>
                              <Input
                                label="Allocated Petty Cash"
                                type="text"
                                placeholder="00"
                                name="allocatedPettyCash"
                                value={values.allocatedPettyCash}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.allocatedPettyCash}
                                touched={touched.allocatedPettyCash}
                                disabled
                              />
                            </Col>

                            <Col md={6}>
                              <Input
                                label="Filled Fuel (ltrs.)"
                                type="text"
                                placeholder="00"
                                name="total_filled_fuel"
                                value={values.total_filled_fuel}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.total_filled_fuel}
                                touched={touched.total_filled_fuel}
                                disabled
                              />
                            </Col>

                            <Col md={6}>
                              <Input
                                label="Closing Fuel (ltrs.)"
                                type="text"
                                placeholder="00"
                                name="closing_fuel"
                                value={values.closing_fuel}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.closing_fuel}
                                touched={touched.closing_fuel}
                                disabled={false}
                              />
                            </Col>
                          </Row>
                        </div>

                        <div className="content_box">
                          <h6>Petty Cash Detail</h6>

                          <TripPettyCash
                            tripPettyCash={tripDetailsData?.trip_petty_cash}
                            vehicle_id={tripDetailsData?.vehicle_id}
                            trip_start_at={tripDetailsData?.actual_start_at || tripDetailsData?.started_at || tripDetailsData?.dispatch_at || tripDetailsData?.start_at}
                            isTripCompleted={isTripCompleted}
                            disablePettyCashActions={disablePettyCashActions}
                            pettyCashLockReason={pettyCashLockReason}
                            allocatedPettyCashAmount={tripDetailsData?.allocated_petty_cash || 0}
                            totalPettyCashAmount={totalPettyCashAmount}
                          />
                        </div>

                        <div className="content_box">
                          <h6>Trip Expenses</h6>

                          <TripExpenses
                            tripExpenses={tripDetailsData?.trip_expenses}
                            vehicle_id={tripDetailsData?.vehicle_id}
                            driver_id={tripDetailsData?.primary_driver?.id}
                            isTripCompleted={isTripCompleted}
                            totalPettyCashAmount={totalPettyCashAmount}
                            totalTripExpenseAmount={totalTripExpenseAmount}
                            remainingPettyCashAmount={remainingPettyCashAmount}
                            canManageTripExpenses={canManageTripExpenses}
                            tripExpensesLockReason={tripExpensesLockReason}
                          />
                        </div>

                        <div className="content_box">
                          <h6>Driver & Helper Details</h6>
                          <p>Main information for this trip record.</p>

                          <Row>
                            <Col md={4}>
                              <Select
                                label="First Driver"
                                placeholder="Select Driver"
                                options={userOptions}
                                loading={loading}
                                name="firstDriver"
                                value={firstDriverOption}
                                onChange={(opt) =>
                                  setFieldValue("firstDriver", opt ? opt.value : "")
                                }
                                onBlur={handleBlur}
                                error={errors.firstDriver}
                                touched={touched.firstDriver}
                                isSearch={true}
                              />
                            </Col>

                            <Col md={4}>
                              <PhoneNumberInput
                                label="First Driver Contact"
                                name="firstDriverContact"
                                value={values.firstDriverContact}
                                setFieldValue={setFieldValue}
                                onBlur={handleBlur}
                                error={errors.firstDriverContact}
                                touched={touched.firstDriverContact}
                              />
                            </Col>

                            <Col md={4}>
                              <Select
                                label="Supplementary Driver"
                                placeholder="Select Driver"
                                options={userOptions}
                                loading={loading}
                                name="supplementaryDriver"
                                value={supplementaryDriverOption}
                                onChange={(opt) =>
                                  setFieldValue("supplementaryDriver", opt ? opt.value : "")
                                }
                                onBlur={handleBlur}
                                error={errors.supplementaryDriver}
                                touched={touched.supplementaryDriver}
                                isSearch={true}
                                required={false}
                              />
                            </Col>

                            <Col md={4}>
                              <PhoneNumberInput
                                label="Supplementary Driver Contact"
                                name="supplementaryDriverContact"
                                value={values.supplementaryDriverContact}
                                setFieldValue={setFieldValue}
                                onBlur={handleBlur}
                                error={errors.supplementaryDriverContact}
                                touched={touched.supplementaryDriverContact}
                                required={false}
                              />
                            </Col>

                            <Col md={4}>
                              <Select
                                label="Helper"
                                placeholder="Select Helper"
                                options={userOptions}
                                loading={loading}
                                name="helper"
                                value={helperOption}
                                onChange={(opt) =>
                                  setFieldValue("helper", opt ? opt.value : "")
                                }
                                onBlur={handleBlur}
                                error={errors.helper}
                                touched={touched.helper}
                                isSearch={true}
                                required={false}
                              />
                            </Col>

                            <Col md={4}>
                              <PhoneNumberInput
                                label="Helper Contact"
                                name="helperContact"
                                value={values.helperContact}
                                setFieldValue={setFieldValue}
                                onBlur={handleBlur}
                                error={errors.helperContact}
                                touched={touched.helperContact}
                                required={false}
                              />
                            </Col>

                            <Col md={4}>
                              <Select
                                label="Uploaded By"
                                placeholder="Select User"
                                options={userOptions}
                                loading={loading}
                                name="uploadedBy"
                                value={
                                  userOptions?.find(
                                    (o) => String(o.value) === String(values.uploadedBy)
                                  ) || null
                                }
                                onChange={(opt) =>
                                  setFieldValue("uploadedBy", opt ? opt.value : "")
                                }
                                onBlur={handleBlur}
                                error={errors.uploadedBy}
                                touched={touched.uploadedBy}
                                isSearch={false}
                                isDisabled={true}
                              />
                            </Col>

                            <Col md={4}>
                              <Input
                                label="Upload Date"
                                type="date"
                                name="uploadDate"
                                value={values.uploadDate}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.uploadDate}
                                touched={touched.uploadDate}
                                disabled
                              />
                            </Col>
                          </Row>
                        </div>

                        <div className="content_box">
                          <h6>Issue Reported</h6>
                          {tripDetailsData?.open_issues?.length > 0 ? (
                            <div className="issue_report_list">
                              {tripDetailsData?.open_issues?.map((issue, index) => (
                                <div className="issue_item" key={index}>
                                  <div className="d-flex justify-content-between gap-2">
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: issue?.description,
                                      }}
                                    />
                                    <sub>{formatDate(issue?.reported_at)}</sub>
                                  </div>

                                  <Status
                                    status={issue?.status}
                                    className={issue?.status}
                                    isIcon={false}
                                    icon={null}
                                  />
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p style={{ textAlign: "center", fontSize: "17px", margin: "20px 0", fontStyle: "italic" }}>
                              No issues reported
                            </p>
                          )}
                        </div>
                        {/* Removed Payment Details section */}
                      </>
                    )}

                    {activeTab === "history" && (
                      <div className="content_box">
                        <TripHistoryTimeline />
                      </div>
                    )}

                    {activeTab === "violations" && (
                      <div className="content_box">
                        <TripViolations />
                      </div>
                    )}

                    {activeTab === "inspection" && (
                      <div className="content_box">
                        <TripInspection />
                      </div>
                    )}
                  </Col>

                  <Col md={3}>
                    <div className="service_cost_summary_details trip_details_sidebar">
                      <div className="service_cost_summary mb-3">
                        <h6>Cost Summary</h6>
                        <p>Financial breakdown of the trip.</p>
                        <div className="service_item">
                          <div>Fuel Cost</div>
                          <div>PKR {tripDetailsData?.fuelCost}</div>
                        </div>

                        {tripDetailsData?.total_trip_driver_expenses?.expenses?.map((expense, index) => (
                          <div className="service_item" key={index}>
                            <div>{expense.title}</div>
                            <div>PKR {expense.total_amount}</div>
                          </div>
                        ))}

                        <div className="total">
                          <div>Total</div>
                          <span>PKR {calculateTripTotalExpenses}</span>
                        </div>
                      </div>

                      <LogsComments />
                    </div>
                  </Col>
                </Row>
              </div>

              <StatusModal
                open={statusModalOpen}
                onClose={closeStatusModal}
                onConfirm={handleConfirmStatusChange}
                status={pendingStatus}
                loading={statusChangeLoading}
                titleText={statusModalConfig.titleText}
                description={statusModalConfig.description}
                showConfirmButton={statusModalConfig.showConfirmButton}
                confirmText={statusModalConfig.confirmText}
                cancelText={statusModalConfig.cancelText}
              />

              <TripCompleteModal
                open={tripCompleteModalOpen}
                onClose={() => {
                  setTripCompleteModalOpen(false);
                  pendingStatusRef.current = "";
                  setPendingStatus(null);
                }}
                onConfirm={handleTripComplete}
                loading={statusChangeLoading}
                initialValues={{
                  ...values,
                  delivered_qty: "",
                  shortage_qty: "",
                  relief_qty: "",
                  totalQuantity: Number(
                    tripDetailsData?.total_load ??
                    tripDetailsData?.total_quantity ??
                    tripDetailsData?.trip_load_sum?.total_load ??
                    tripDetailsData?.quantity ??
                    (Array.isArray(tripDetailsData?.trip_stops || tripDetailsData?.stops || values?.stops)
                      ? (tripDetailsData?.trip_stops || tripDetailsData?.stops || values?.stops)
                          .filter((s) => s.stop_type === "load")
                          .reduce((sum, s) => sum + (Number(s.quantity) || 0), 0) ||
                        (tripDetailsData?.trip_stops || tripDetailsData?.stops || values?.stops)
                          .reduce((sum, s) => sum + (Number(s.quantity) || 0), 0)
                      : 0)
                  ) || 0,
                }}
              />

              <TripCloseModal
                open={tripCloseModalOpen}
                onClose={closeTripCloseModal}
                onConfirm={handleTripClose}
                loading={statusChangeLoading}
                detailLoading={tripCloseDetailLoading}
                detailData={tripCloseDetailData}
                tripContext={tripDetailsData}
                tripIdDisplay={(tripDetailsData?.data || tripDetailsData)?.trip_id || (tripDetailsData?.data || tripDetailsData)?.tripId || ""}
                customerOptions={customerOptions}
                costCenterOptions={costCenterDropdownData}
                userOptions={userOptions}
                vehicleTypeLabel={
                  contractVehicleTypeOptions?.find(
                    (option) => String(option?.value) === String(values.vehicleType)
                  )?.label || ""
                }
              />
            </>
          );
        }}
      </Formik>
    </>
  );
};

export default TripDetails;
